// analytics.js — the app's one and only GA4 tap (Brief 016).
//
// index.html loads gtag for the SpeechGenie property (G-C29H757D12) and fires
// the default pageview; this module is the single place the app sends CUSTOM
// events from, so the property/param scheme has exactly one place to change.
//
// Everything here is best-effort. `gtag` is absent or blocked in plenty of real
// contexts — ad-block, privacy mode, a partitioned third-party iframe, an
// offline load — and analytics must NEVER be the thing that blanks the app
// (Gotcha 2, and the demo's iOS-iframe boot fragility makes that a live risk).
// So every call is wrapped: a missing or throwing gtag is a silent no-op.
//
// Architecture rule #1 (CLAUDE.md): engines stay pure. `track()` is called from
// screens and app boot ONLY — never from `app/engines/*`.
//
// House rule — NO PII: params are enums, bools and counts. Never the learner's
// email, never the target words/phrases being learned, never free text.
//
// Internal traffic is TAGGED, never suppressed (Brief 023). Chris's own test
// sessions send every event exactly as a learner's do — DebugView has to keep
// working, and the BigQuery export has to keep agreeing with the GA4 UI — and
// carry `traffic_type: "internal"` so GA4's own data filter can exclude them at
// query time. See `_internal` below.
//
// Every event also carries `embed_src` — which page the demo is embedded in —
// when the parent page's iframe `src` supplies one (Brief 025). Unlike
// `traffic_type` it does NOT persist: it describes this load, not this person.
// See `_embedSrc` below.
//
// Events fired by the demo (all snake_case, GA4-style):
//   demo_open       — once per page load (app boot)
//   lesson_start    — the learner leaves the Start prompt into the lesson
//   item_attempt    — one tile answer      { correct, round }
//   item_mastered   — an item reached the mastery streak { round }
//   lesson_complete — the lesson reached the complete state { mastered_count }
//   continue_click  — the completion "Continue" CTA { destination }
//   language_toggle — the header direction toggle { to_language }
//   info_reveal     — the ⓘ translation reveal
//   tile_enlarge    — tap-to-enlarge on a tile
//   reveal_view     — the reveal pane mounted (Brief 020)
//   run_abandon     — the page went hidden (Brief 020, corrected by 022)
//                     { stage, hide_seq, attempts, … }, sent by BEACON — see
//                     trackBeacon below. Fires on EVERY hide, numbered by
//                     `hide_seq`; the row that means "they left" is the highest
//                     `hide_seq` with no matching `run_resume`
//   run_resume      — they came back (Brief 022) { hide_seq, attempts }. Normal
//                     transport: the page is alive. A `run_abandon` whose
//                     `hide_seq` this matches was a glance, not a departure

import * as lang from "../i18n/language-manager.js";
import { VERSION } from "../version.js";

/** Names already sent via `trackOnce` this page load. @type {Set<string>} */
const _onceFired = new Set();

/**
 * Is the demo running inside an iframe? Reading `window.top` cross-origin can
 * throw — and a throw MEANS a cross-origin top, i.e. embedded — so `true` is
 * the right default on failure (Gotcha 6).
 * @returns {boolean}
 */
function isEmbedded() {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}

/**
 * Is this one of Chris's own sessions? (Brief 023.)
 *
 * The flag arrives as `?internal=1` on the app's OWN query string, and `?internal=0`
 * clears it. It cannot be discovered any other way from in here: browsers partition
 * `localStorage` for a third-party iframe by top-level site, so a flag set by visiting
 * `l2b.speechgenie.online` directly is invisible to the same app embedded in a
 * speechgenie.co page — and `document.referrer` arrives as a bare origin under the
 * default `strict-origin-when-cross-origin` policy, path and query stripped. So the
 * parent page reads the flag and appends it to the iframe `src`; a Custom HTML block
 * on `/third-ear/` and `/l2b-demo/` already does exactly that.
 *
 * Resolved ONCE, at module scope, for two reasons: `commonParams()` runs on every
 * event and this is a hot path, and a mid-session storage change must not split one
 * session across two traffic types.
 *
 * The try/catch is load-bearing, not padding. `localStorage` THROWS outright in some
 * private-browsing and blocked-storage contexts (and `location` is undeclared under
 * the node test environment); an uncaught throw at module scope would take the whole
 * analytics module — and with it every event on the page — down with it.
 */
const _internal = (() => {
  try {
    const q = new URLSearchParams(location.search).get("internal");
    if (q === "1") localStorage.setItem("l2b_internal", "1");
    if (q === "0") localStorage.removeItem("l2b_internal");
    return localStorage.getItem("l2b_internal") === "1";
  } catch {
    return false;
  }
})();

/**
 * WHICH page is the demo embedded in? (Brief 025.)
 *
 * `embedded` already separates an iframe from a direct open; this says which iframe.
 * Without it the campaign is unmeasurable: a learner who lands on `/third-ear/` and
 * presses launch is TWO GA4 users — one on speechgenie.co, one on l2b.speechgenie.online —
 * with different cookies and client IDs and nothing in the GA4 UI joining them, and every
 * iframe session from every speechgenie.co page pools into one undifferentiated bucket.
 *
 * The value is baked into the iframe's `src` in the parent page's markup — NOT appended
 * by a script, the way `internal=1` is. Rewriting an iframe's `src` RELOADS the iframe,
 * so a scripted append would make every visitor fire `demo_open` twice, once untagged,
 * corrupting the exact measurement this exists to take.
 *
 * THIS MUST NOT PERSIST. That is the deliberate inversion of `_internal` above, and the
 * one place a reader who knows 023 will reach for `localStorage` out of habit. `traffic_type`
 * is sticky because it describes a PERSON; `embed_src` describes THIS LOAD, and a value
 * remembered from an earlier load would attribute a later direct visit to a page the
 * learner never came from — silently, and in the direction that flatters the campaign.
 *
 * Validated, not trusted: it arrives in a URL, so anyone can put anything in it, and a
 * reporting dimension with unbounded cardinality degrades into `(other)` for everyone.
 * A value failing the pattern is DROPPED — not truncated, not sanitised — because a
 * mangled value that still reports is worse than a missing one: it looks like data.
 *
 * Resolved once at module scope for `_internal`'s first reason: `commonParams()` runs on
 * every event. The try/catch is for the same reason as well — `location` is undeclared
 * under the node test environment, and a throw here would take every event on the page
 * down with it.
 */
const _embedSrc = (() => {
  try {
    const v = new URLSearchParams(location.search).get("embed_src");
    return v && /^[a-z0-9-]{1,32}$/.test(v) ? v : null;
  } catch {
    return null;
  }
})();

/**
 * The validated `embed_src` for this load, or null — for the signup POST (Brief 026),
 * which must send the SAME value the events carry rather than re-read and re-validate
 * the query string on its own. A getter rather than an exported binding so the module
 * keeps one resolution, one pattern, and one place to change either.
 * @returns {string | null}
 */
export function embedSrc() {
  return _embedSrc;
}

/**
 * Params attached to every event: which direction the learner is running, which
 * build, and whether this is the marketing embed or a direct open.
 * @returns {Record<string, unknown>}
 */
function commonParams() {
  /** @type {Record<string, unknown>} */
  const out = { demo_build: VERSION, embedded: isEmbedded() };
  try {
    const axes = lang.current();
    out.target_language = axes.targetLanguage;
    out.l1_language = axes.motherTongue;
  } catch {
    /* language manager not hydrated yet — send the event without the axes */
  }
  // ABSENT for normal traffic, not `false` and not `""`. The name and the value
  // are the exact strings GA4's built-in internal-traffic filter matches on, so
  // this needs no custom dimension — and GA4 expects the parameter to be MISSING
  // for everyone else. An empty string is a value, not an absence.
  if (_internal) out.traffic_type = "internal";
  // ABSENT too, and for a reason of its own beyond the house rule: `embedded: true`
  // with NO `embed_src` means the demo is framed by a page nobody tagged — someone
  // else's site, or one of ours that got missed. That is a diagnostic, and an empty
  // string would destroy it.
  if (_embedSrc) out.embed_src = _embedSrc;
  return out;
}

/**
 * Send one GA4 event. Silently no-ops when gtag is absent or blocked, and never
 * throws — a failed analytics call must never break a user action.
 * @param {string} name  snake_case GA4 event name
 * @param {Record<string, unknown>} [params]  enums / bools / counts only — no PII
 */
export function track(name, params) {
  try {
    const gtag = /** @type {any} */ (globalThis).gtag;
    if (typeof gtag !== "function") return;
    gtag("event", name, { ...commonParams(), ...(params ?? {}) });
  } catch {
    /* gtag blocked, throwing, or the page is mid-teardown — drop the event */
  }
}

/**
 * `track()`, but asking GA4 to hand the hit to `navigator.sendBeacon` rather
 * than an image/XHR (Brief 020). Used ONLY on the unload path (`run_abandon`).
 *
 * Why it matters more than it looks: a normal gtag call issued from `pagehide`
 * or a `visibilitychange`→hidden is dropped often enough to bias the dataset
 * toward the people who STAYED — precisely the population the abandon event
 * exists to measure around. A beacon is queued by the browser and survives the
 * teardown, so early leavers actually land.
 *
 * `transport_type` is a gtag directive, not a dimension: GA4 strips it from the
 * event rather than reporting it, so it costs no custom parameter. Same
 * best-effort contract as `track()` — a missing or throwing gtag is a no-op.
 * @param {string} name
 * @param {Record<string, unknown>} [params]  enums / bools / counts only — no PII
 */
export function trackBeacon(name, params) {
  track(name, { ...(params ?? {}), transport_type: "beacon" });
}

/**
 * `track()`, but at most once per page load for this event name. Used for the
 * session-scoped events (`demo_open`) so a re-render or a re-entered boot path
 * can't inflate counts (Gotcha 4). Lesson-scoped dedupe lives in the screen,
 * which knows when a lesson restarts.
 * @param {string} name
 * @param {Record<string, unknown>} [params]
 */
export function trackOnce(name, params) {
  if (_onceFired.has(name)) return;
  _onceFired.add(name);
  track(name, params);
}
